import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationError, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private readonly updateCheckIntervalMs = 60_000;
  private monitoring = false;
  private reloadPromptOpen = false;
  private updateCheckInFlight = false;
  private loadedMainBundleUrl: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  startMonitoring(): void {
    if (this.monitoring) {
      return;
    }

    this.monitoring = true;
    this.loadedMainBundleUrl = this.getLoadedMainBundleUrl();
    this.router.events.pipe(
      filter((event): event is NavigationError => event instanceof NavigationError),
    ).subscribe(event => this.handleNavigationError(event));

    this.checkForUpdate();
    this.document.defaultView?.addEventListener?.('focus', () => this.checkForUpdate());
    this.document.defaultView?.setInterval?.(() => this.checkForUpdate(), this.updateCheckIntervalMs);
  }

  private handleNavigationError(event: NavigationError): void {
    if (this.reloadPromptOpen || !this.isLazyChunkLoadError(event.error)) {
      return;
    }

    this.promptReload();
  }

  private checkForUpdate(): void {
    if (this.reloadPromptOpen || this.updateCheckInFlight || !this.loadedMainBundleUrl) {
      return;
    }

    const window = this.document.defaultView;
    if (!window?.fetch) {
      return;
    }

    this.updateCheckInFlight = true;
    window.fetch('/', { cache: 'no-store' })
      .then(response => response.ok ? response.text() : null)
      .then(indexHtml => {
        const deployedMainBundleUrl = indexHtml ? this.getMainBundleUrl(indexHtml) : null;
        if (deployedMainBundleUrl && deployedMainBundleUrl !== this.loadedMainBundleUrl) {
          this.promptReload();
        }
      })
      .catch(() => {
        // Offline and transient failures should not interrupt the current session.
      })
      .finally(() => {
        this.updateCheckInFlight = false;
      });
  }

  private promptReload(): void {
    if (this.reloadPromptOpen) {
      return;
    }

    this.reloadPromptOpen = true;
    const snackBarRef = this.snackBar.open(
      'Backoffice was updated. Reload to continue.',
      'Reload',
      { duration: 0 },
    );

    snackBarRef.onAction().pipe(take(1)).subscribe(() => {
      this.document.defaultView?.location.reload();
    });
    snackBarRef.afterDismissed().pipe(take(1)).subscribe(() => {
      this.reloadPromptOpen = false;
    });
  }

  private getLoadedMainBundleUrl(): string | null {
    const script = this.document.querySelector?.<HTMLScriptElement>('script[src*="main-"]');
    return script?.src || null;
  }

  private getMainBundleUrl(indexHtml: string): string | null {
    const match = /<script[^>]+src=["']([^"']*main-[^"']+\.js)["']/i.exec(indexHtml);
    return match ? new URL(match[1], this.document.baseURI).href : null;
  }

  private isLazyChunkLoadError(error: unknown): boolean {
    const message = error instanceof Error
      ? `${error.name}: ${error.message}`
      : String(error);

    return [
      'chunkloaderror',
      'loading chunk',
      'failed to fetch dynamically imported module',
      'failed to load module script',
      'importing a module script failed',
      'error loading dynamically imported module',
      'expected a javascript-or-wasm module script',
    ].some(fragment => message.toLowerCase().includes(fragment));
  }
}
